import React from 'react';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { ListsyncFooter } from '@/components/listsync/Footer';

const ListsyncPrivacyPage: React.FC = () => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <>
      <Head>
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@600&display=swap"
          rel="stylesheet"
        />
        <style
          dangerouslySetInnerHTML={{
            __html: `
              .listsync-nav a:hover { color: #4F46E5 !important; }
              html { scroll-behavior: smooth; }
            `
          }}
        />
        <title>Privacy Policy - Listsync</title>
        <meta
          name="description"
          content="Website Privacy Policy for Listsync"
        />
      </Head>
      <div
        style={{
          fontFamily: 'system-ui, -apple-system, sans-serif',
          backgroundColor: '#ffffff',
          minHeight: '100vh'
        }}
      >
        {/* Header */}
        <header
          style={{
            padding: isMobile ? '16px 20px' : '24px 60px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: 'transparent',
            position: 'relative',
            zIndex: 10,
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%',
            flexWrap: isMobile ? 'wrap' : 'nowrap'
          }}
        >
          <Link
            href="/"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <Image
              src="/listsync/listsync-logo.svg"
              alt="Listsync Logo"
              width={isMobile ? 120 : 160}
              height={isMobile ? 41 : 55}
              style={{ height: 'auto' }}
            />
          </Link>
        </header>

        {/* Main Content */}
        <main
          style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
            backgroundColor: '#ffffff',
            minHeight: 'calc(100vh - 200px)',
            color: '#1a1a1a'
          }}
        >
          <div
            style={{
              maxWidth: '1200px',
              margin: '0 auto',
              padding: '60px 40px'
            }}
          >
            {/* Title Section */}
            <div style={{ textAlign: 'center', marginBottom: '60px' }}>
              <h1
                style={{
                  fontSize: '42px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  letterSpacing: '-0.02em'
                }}
              >
                Website Privacy Policy
              </h1>
              <p
                style={{
                  fontSize: '20px',
                  color: '#4b5563',
                  marginBottom: '8px'
                }}
              >
                NESTFLO LIMITED, trading as Listsync
              </p>
              <p
                style={{
                  fontSize: '18px',
                  color: '#64748b',
                  marginBottom: '8px'
                }}
              >
                Privacy Policy
              </p>
              <p style={{ fontSize: '18px', color: '#64748b' }}>Dec 2025</p>
            </div>

            {/* Content */}
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                1. Who we are
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We are NESTFLO LIMITED trading as Listsync is a limited
                  company registered in England and Wales, company number
                  16878995 and registered address at Filwood Green Business
                  Park, 1 Filwood Park Ln, Bristol, England, BS4 1ET.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We provide automated synchronization of property listings
                  between Property Management Software/CRMs and property
                  portals.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You can contact us by email{' '}
                  <a
                    href="mailto:hello@listsync.co.uk"
                    style={{
                      color: '#4F46E5',
                      textDecoration: 'underline'
                    }}
                  >
                    hello@listsync.co.uk
                  </a>
                  .
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                2. Scope of this policy
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We respect your privacy and we are committed to protecting
                  your personal data. This privacy policy will inform you as to
                  how we look after your personal data when you visit{' '}
                  <a
                    href="https://listsync.co.uk/"
                    style={{
                      color: '#4F46E5',
                      textDecoration: 'underline'
                    }}
                  >
                    https://listsync.co.uk/
                  </a>{' '}
                  (&ldquo;Website&rdquo;), (regardless of where you visit it
                  from) and when you purchase our products and services. This
                  privacy policy will also tell you about your privacy rights
                  and how the law protects you.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  It is important that you read this privacy policy together
                  with any other privacy policy or fair processing policy we may
                  provide on specific occasions when we are collecting or
                  processing personal data about you so that you are fully aware
                  of how and why we are using your data. This privacy policy
                  supplements other notices and privacy policies and is not
                  intended to override them.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                3. Controller
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We are the controller and responsible for your personal data.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We have appointed a data privacy manager who is responsible
                  for overseeing questions in relation to this privacy policy.
                  If you have any questions about this privacy policy, including
                  any requests to exercise your legal rights, please contact the
                  data privacy manager using the contact details set out above.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                4. Contacting the Information Commissioner&apos;s Office
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  You have the right to make a complaint at any time to the
                  Information Commissioner&apos;s Office (ICO), the UK regulator
                  for data protection issues (
                  <a
                    href="https://www.ico.org.uk"
                    style={{
                      color: '#4F46E5',
                      textDecoration: 'underline'
                    }}
                  >
                    www.ico.org.uk
                  </a>
                  ).
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We would, however, appreciate the chance to deal with your
                  concerns before you approach the ICO so please contact us in
                  the first instance.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                5. Changes to the privacy policy and your duty to inform us of
                changes
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We keep our privacy policy under regular review. This version
                  was last updated on 01 Dec 2025.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  It is important that the personal data we hold about you is
                  accurate and current. Please keep us informed if your personal
                  data changes during your relationship with us.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                6. Third-party links
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  The Website may include links to third-party websites,
                  plug-ins and applications. Clicking on those links or enabling
                  those connections may allow third parties to collect or share
                  data about you.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We do not control these third-party websites and are not
                  responsible for their privacy statements. When you leave our
                  Website, we encourage you to read the privacy policy of every
                  website you visit.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                7. The data we collect about you
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Personal data, or personal information, means any information
                  about an individual from which that person can be identified.
                  It does not include data where the identity has been removed
                  (anonymous data).
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We may collect, use, store and transfer different kinds of
                  personal data about you which we have grouped together as
                  follows:
                </p>

                <div style={{ marginLeft: '24px', marginBottom: '24px' }}>
                  <p style={{ marginBottom: '16px' }}>
                    <strong>Identity Data:</strong> includes first name, maiden
                    name, last name, username or similar identifier, marital
                    status, date of birth and gender.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>Contact Data:</strong> includes billing address,
                    delivery address, email address and telephone numbers.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>Technical Data:</strong> includes internet protocol
                    (IP) address, your login data, browser type and version,
                    time zone setting and location, browser plug-in types and
                    versions, operating system and platform, and other
                    technology on the devices you use to access the Website, and
                    our products and services.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>Profile Data:</strong> includes your username and
                    password, purchases or orders made by you, your interests,
                    preferences, feedback and survey responses.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>Usage Data:</strong> includes information about how
                    you use the Website, and our products and services.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>Marketing and Communications Data:</strong> includes
                    your preferences in receiving marketing from us and our
                    third parties and your communication preferences.
                  </p>
                </div>

                <p style={{ marginBottom: '24px' }}>
                  We also collect, use and share Aggregated Data such as
                  statistical or demographic data for any purpose. Aggregated
                  Data could be derived from your personal data but is not
                  considered personal data in law as this data will not directly
                  or indirectly reveal your identity. However, if we combine or
                  connect Aggregated Data with your personal data so that it can
                  directly or indirectly identify you, we treat the combined
                  data as personal data which will be used in accordance with
                  this privacy policy.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Except under clause 7.4 above, we do NOT collect any Special
                  Categories of Personal Data about you (this includes details
                  about your race or ethnicity, religious or philosophical
                  beliefs, sex life, sexual orientation, political opinions,
                  trade union membership, genetic and biometric data).
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                8. If you fail to provide personal data
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Where we need to collect personal data by law, or under the
                  terms of a contract we have with you (for example, a contract
                  to provide you our products and services), and you fail to
                  provide that data when requested, we may not be able to
                  perform the contract we have or are trying to enter into with
                  you. In this case, we may have to cancel a product or service
                  you have with us, but we will notify you if this is the case
                  at the time.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                9. How is your personal data collected?
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We use different methods to collect data from and about you
                  including through:
                </p>

                <div style={{ marginLeft: '24px', marginBottom: '24px' }}>
                  <p style={{ marginBottom: '16px' }}>
                    <strong>Direct interactions.</strong> You may give us your
                    personal data by filling in forms by corresponding with us
                    by phone, email or otherwise. This includes personal data
                    you provide when you:
                    <br />• apply for our products or services;
                    <br />• create an account on the Website;
                    <br />• authorise marketing to be sent to you;
                    <br />• enter a competition, promotion or survey; or
                    <br />• give us feedback or contact us.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>Automated technologies or interactions.</strong> As
                    you interact with our Website, we will automatically collect
                    Technical Data about your equipment, browsing actions and
                    patterns. We collect this personal data by using cookies,
                    server logs and other similar technologies. We may also
                    receive Technical Data about you if you visit other websites
                    employing our cookies.
                  </p>

                  <p style={{ marginBottom: '16px' }}>
                    <strong>
                      Third parties or publicly available sources.
                    </strong>{' '}
                    We will receive personal data about you from various third
                    parties and public sources (sometimes based outside the UK),
                    like:
                    <br />• analytics providers such as Google;
                    <br />• advertising networks;
                    <br />• search information providers;
                    <br />• providers of technical, payment and delivery
                    services;
                    <br />• data brokers or aggregators; and
                    <br />• publicly available sources.
                  </p>
                </div>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                10. How we use your personal data
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We will only use your personal data when the law allows us to.
                  Most commonly, we will use your personal data in the following
                  circumstances:
                </p>

                <div style={{ marginLeft: '24px', marginBottom: '24px' }}>
                  <p style={{ marginBottom: '8px' }}>
                    • Where we need to perform the contract we are about to
                    enter into or have entered into with you.
                  </p>
                  <p style={{ marginBottom: '8px' }}>
                    • Where it is necessary for our legitimate interests (or
                    those of a third party) and your interests and fundamental
                    rights do not override those interests.
                  </p>
                  <p style={{ marginBottom: '8px' }}>
                    • Where we need to comply with a legal obligation.
                  </p>
                </div>

                <p style={{ marginBottom: '24px' }}>
                  Generally, we do not rely on consent as a legal basis for
                  processing your personal data although we will get your
                  consent before sending third party direct marketing
                  communications to you via email or text message. You have the
                  right to withdraw consent to marketing at any time by
                  contacting us.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                11. Purposes for which we will use your personal data
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We have set out below, a description of the ways we plan to
                  use your personal data, and which of the legal bases we rely
                  on to do so. We have also identified what our legitimate
                  interests are where appropriate.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Note that we may process your personal data for more than one
                  lawful ground depending on the specific purpose for which we
                  are using your data.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Please contact us if you need details about the specific legal
                  ground we are relying on to process your personal data where
                  more than one ground has been set out in the table below.
                </p>

                <div
                  style={{
                    overflowX: 'auto',
                    marginTop: '24px',
                    marginBottom: '24px'
                  }}
                >
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '14px'
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: '#f3f4f6' }}>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            border: '1px solid #d1d5db',
                            fontWeight: '600'
                          }}
                        >
                          Purpose/Activity
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            border: '1px solid #d1d5db',
                            fontWeight: '600'
                          }}
                        >
                          Type of data
                        </th>
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            border: '1px solid #d1d5db',
                            fontWeight: '600'
                          }}
                        >
                          Lawful basis for processing (including basis of
                          legitimate interest)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To register you as a new customer
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          Performance of a contract with you
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To process and deliver your order including:
                          <br />
                          (a) Manage payments, fees and charges
                          <br />
                          (b) Collect and recover money owed to us
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                          <br />
                          (e) Marketing and Communications
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Performance of a contract with you
                          <br />
                          (b) Necessary for our legitimate interests (to recover
                          debts due to us)
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To manage our relationship with you which will
                          include:
                          <br />
                          (a) Notifying you about changes to our terms or
                          privacy policy
                          <br />
                          (b) Asking you to leave a review or take a survey
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                          <br />
                          (c) Profile
                          <br />
                          (d) Marketing and Communications
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Performance of a contract with you
                          <br />
                          (b) Necessary to comply with a legal obligation
                          <br />
                          (c) Necessary for our legitimate interests (to keep
                          our records updated and to study how customers use our
                          products/services)
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To enable you to partake in a prize draw, competition
                          or complete a survey
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                          <br />
                          (c) Profile
                          <br />
                          (d) Usage
                          <br />
                          (e) Marketing and Communications
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Performance of a contract with you
                          <br />
                          (b) Necessary for our legitimate interests (to study
                          how customers use our products/services, to develop
                          them and grow our business)
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To administer and protect our business and this
                          website (including troubleshooting, data analysis,
                          testing, system maintenance, support, reporting and
                          hosting of data)
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                          <br />
                          (c) Technical
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Necessary for our legitimate interests (for
                          running our business, provision of administration and
                          IT services, network security, to prevent fraud and in
                          the context of a business reorganisation or group
                          restructuring exercise)
                          <br />
                          (b) Necessary to comply with a legal obligation
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To deliver relevant website content and advertisements
                          to you and measure or understand the effectiveness of
                          the advertising we serve to you
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                          <br />
                          (c) Profile
                          <br />
                          (d) Usage
                          <br />
                          (e) Marketing and Communications
                          <br />
                          (f) Technical
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          Necessary for our legitimate interests (to study how
                          customers use our products/services, to develop them,
                          to grow our business and to inform our marketing
                          strategy)
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To use data analytics to improve our website,
                          products/services, marketing, customer relationships
                          and experiences
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Technical
                          <br />
                          (b) Usage
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          Necessary for our legitimate interests (to define
                          types of customers for our products and services, to
                          keep our website updated and relevant, to develop our
                          business and to inform our marketing strategy)
                        </td>
                      </tr>
                      <tr>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          To make suggestions and recommendations to you about
                          goods or services that may be of interest to you
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          (a) Identity
                          <br />
                          (b) Contact
                          <br />
                          (c) Technical
                          <br />
                          (d) Usage
                          <br />
                          (e) Profile
                          <br />
                          (f) Marketing and Communications
                        </td>
                        <td
                          style={{
                            padding: '12px',
                            border: '1px solid #d1d5db',
                            verticalAlign: 'top'
                          }}
                        >
                          Necessary for our legitimate interests (to develop our
                          products/services and grow our business)
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                12. Marketing
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We strive to provide you with choices regarding certain
                  personal data uses, particularly around marketing and
                  advertising.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We may use your Identity, Contact, Technical, Usage and
                  Profile Data to form a view on what we think you may want or
                  need, or what may be of interest to you. This is how we decide
                  which products, services and offers may be relevant for you
                  (we call this marketing).
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You will receive marketing communications from us if you have
                  requested information from us or agreed to receive such
                  marketing communications.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We will get your express opt-in consent before we share your
                  personal data with any third party for marketing purposes.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You can ask us or third parties to stop sending you marketing
                  messages at any time by following the opt-out links on any
                  marketing message sent to you or by contacting us at any time.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Where you opt out of receiving these marketing messages, this
                  will not apply to personal data provided to us as a result of
                  a product/service purchase, warranty registration,
                  product/service experience or other transactions.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                13. Cookies
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  You can set your browser to refuse all or some browser
                  cookies, or to alert you when websites set or access cookies.
                  If you disable or refuse cookies, please note that some parts
                  of the Website may become inaccessible or not function
                  properly.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  For more information about the cookies we use, please see{' '}
                  <a
                    href="https://listsync.co.uk/"
                    style={{
                      color: '#4F46E5',
                      textDecoration: 'underline'
                    }}
                  >
                    https://listsync.co.uk/
                  </a>
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                14. Change of purpose
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We will only use your personal data for the purposes for which
                  we collected it, unless we reasonably consider that we need to
                  use it for another reason and that reason is compatible with
                  the original purpose. If you wish to get an explanation as to
                  how the processing for the new purpose is compatible with the
                  original purpose, please contact us.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  If we need to use your personal data for an unrelated purpose,
                  we will notify you and we will explain the legal basis which
                  allows us to do so.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Please note that we may process your personal data without
                  your knowledge or consent, in compliance with the above rules,
                  where this is required or permitted by law.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                15. Disclosures of your personal data
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We may share your personal data with the following third
                  parties for the purposes set out in this privacy policy.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  HM Revenue &amp; Customs, regulators and other authorities.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Third parties to whom we may choose to sell, transfer or merge
                  parts of our business or our assets. Alternatively, we may
                  seek to acquire other businesses or merge with them. If a
                  change happens to our business, then the new owners may use
                  your personal data in the same way as set out in this privacy
                  policy.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We require all third parties to respect the security of your
                  personal data and to treat it in accordance with the law. We
                  do not allow our third-party service providers to use your
                  personal data for their own purposes and only permit them to
                  process your personal data for specified purposes and in
                  accordance with our instructions.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                16. International transfers
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Some of our external third parties are based outside the UK
                  and the EEA so their processing of your personal data will
                  involve a transfer of data outside the UK and the EEA.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Whenever we transfer your personal data out of the UK and the
                  EEA, we ensure a similar degree of protection is afforded to
                  it by ensuring at least one of the following safeguards is
                  implemented:
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We will only transfer your personal data to countries that
                  have been deemed to provide an adequate level of protection
                  for personal data.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Where we use certain service providers, we may use specific
                  contracts approved for use in the UK which give personal data
                  the same protection it has in the UK.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  Please contact us if you want further information on the
                  specific mechanism used by us when transferring your personal
                  data out of the UK and the EEA.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                17. Data security
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We have put in place appropriate security measures to prevent
                  your personal data from being accidentally lost, used or
                  accessed in an unauthorised way, altered or disclosed. In
                  addition, we limit access to your personal data to those
                  employees, agents, contractors and other third parties who
                  have a business need to know. They will only process your
                  personal data on our instructions and they are subject to a
                  duty of confidentiality.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  We have put in place procedures to deal with any suspected
                  personal data breach and will notify you and any applicable
                  regulator of a breach where we are legally required to do so.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                18. Data retention
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  We will only retain your personal data for as long as
                  reasonably necessary to fulfil the purposes we collected it
                  for, including for the purposes of satisfying any legal,
                  regulatory, tax, accounting or reporting requirements. We may
                  retain your personal data for a longer period in the event of
                  a complaint or if we reasonably believe there is a prospect of
                  litigation in respect to our relationship with you.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  To determine the appropriate retention period for personal
                  data, we consider the amount, nature and sensitivity of the
                  personal data, the potential risk of harm from unauthorised
                  use or disclosure of your personal data, the purposes for
                  which we process your personal data and whether we can achieve
                  those purposes through other means, and the applicable legal,
                  regulatory, tax, accounting or other requirements.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  In some circumstances we will anonymise your personal data (so
                  that it can no longer be associated with you) for research or
                  statistical purposes, in which case we may use this
                  information indefinitely without further notice to you.
                </p>
              </div>

              <h2
                style={{
                  fontSize: '28px',
                  fontWeight: '700',
                  color: '#1a1a1a',
                  marginBottom: '24px',
                  marginTop: '48px'
                }}
              >
                19. Your legal rights
              </h2>

              <div
                style={{
                  color: '#334155',
                  lineHeight: '1.8',
                  fontSize: '16px'
                }}
              >
                <p style={{ marginBottom: '24px' }}>
                  Under certain circumstances, you have rights under data
                  protection laws in relation to your personal data.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have right to request access to your personal data
                  (commonly known as a &ldquo;data subject access
                  request&rdquo;). This enables you to receive a copy of the
                  personal data we hold about you and to check that we are
                  lawfully processing it.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have right to request correction of the personal data that
                  we hold about you. This enables you to have any incomplete or
                  inaccurate data we hold about you corrected, though we may
                  need to verify the accuracy of the new data you provide to us.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have the right to request erasure of your personal data.
                  This enables you to ask us to delete or remove personal data
                  where there is no good reason for us continuing to process it.
                  You also have the right to ask us to delete or remove your
                  personal data where you have successfully exercised your right
                  to object to processing, where we may have processed your
                  information unlawfully or where we are required to erase your
                  personal data to comply with local law. Note, however, that we
                  may not always be able to comply with your request of erasure
                  for specific legal reasons which will be notified to you, if
                  applicable, at the time of your request.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have the right to object to processing of your personal
                  data where we are relying on a legitimate interest (or those
                  of a third party) and there is something about your particular
                  situation which makes you want to object to processing on this
                  ground as you feel it impacts on your fundamental rights and
                  freedoms. You also have the right to object where we are
                  processing your personal data for direct marketing purposes.
                  In some cases, we may demonstrate that we have compelling
                  legitimate grounds to process your information which override
                  your rights and freedoms.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have the right to request restriction of processing of
                  your personal data. This enables you to ask us to suspend the
                  processing of your personal data in the following scenarios:
                  <br />• If you want us to establish the data&apos;s accuracy.
                  <br />• Where our use of the data is unlawful but you do not
                  want us to erase it.
                  <br />• Where you need us to hold the data even if we no
                  longer require it as you need it to establish, exercise or
                  defend legal claims.
                  <br />• You have objected to our use of your data but we need
                  to verify whether we have overriding legitimate grounds to use
                  it.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have the right to request the transfer of your personal
                  data to you or to a third party. We will provide to you, or a
                  third party you have chosen, your personal data in a
                  structured, commonly used, machine-readable format. Note that
                  this right only applies to automated information which you
                  initially provided consent for us to use or where we used the
                  information to perform a contract with you.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  You have the right to withdraw consent at any time where we
                  are relying on consent to process your personal data. However,
                  this will not affect the lawfulness of any processing carried
                  out before you withdraw your consent. If you withdraw your
                  consent, we may not be able to provide certain products or
                  services to you. We will advise you if this is the case at the
                  time you withdraw your consent.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  If you wish to exercise any of the rights set out above,
                  please contact us.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  <strong>No fee usually required:</strong> You will not have to
                  pay a fee to access your personal data (or to exercise any of
                  the other rights). However, we may charge a reasonable fee if
                  your request is clearly unfounded, repetitive or excessive.
                  Alternatively, we could refuse to comply with your request in
                  these circumstances.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  <strong>What we may need from you:</strong> We may need to
                  request specific information from you to help us confirm your
                  identity and ensure your right to access your personal data
                  (or to exercise any of your other rights). This is a security
                  measure to ensure that personal data is not disclosed to any
                  person who has no right to receive it. We may also contact you
                  to ask you for further information in relation to your request
                  to speed up our response.
                </p>

                <p style={{ marginBottom: '24px' }}>
                  <strong>Time limit to respond:</strong> We try to respond to
                  all legitimate requests within one month. Occasionally it
                  could take us longer than a month if your request is
                  particularly complex or you have made a number of requests. In
                  this case, we will notify you and keep you updated.
                </p>
              </div>
            </div>
          </div>
        </main>

        <ListsyncFooter />
      </div>
    </>
  );
};

export default ListsyncPrivacyPage;
